// apps/web/src/pages/QuestionEditPage.tsx
// 新建 / 编辑题目 — Phase 2 实现

import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QuestionForm from "@components/question/QuestionForm";
import { getQuestionApi, addQuestionApi, updateQuestionApi } from "@/api/questions";
import { useAuthStore } from "@/stores/authStore";
import type { QuestionType, CreateQuestionRequest } from "@examfast/shared-types";

export default function QuestionEditPage() {
  const { bankId, qid } = useParams<{ bankId: string; qid?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!qid;

  // 编辑模式：获取题目详情
  const { data: q, isLoading } = useQuery({
    queryKey: ["question", qid],
    queryFn: () => (qid ? getQuestionApi(bankId!, qid) : Promise.resolve(null)),
    enabled: isEdit && !!qid,
  });

  const defaultValues = q
    ? {
        type: q.type as QuestionType,
        content: q.content,
        options: q.options?.map((o) => ({ value: o })) ?? undefined,
        correct_answer: JSON.parse(q.correct_answer),
        explanation: q.explanation ?? undefined,
        chapter: q.chapter ?? undefined,
        difficulty: q.difficulty ?? undefined,
        tags: q.tags ?? undefined,
        qid: q.id,
      }
    : undefined;

  const handleSuccess = () => {
    // 刷新题目列表与题库详情
    queryClient.invalidateQueries({ queryKey: ["questions", bankId] });
    queryClient.invalidateQueries({ queryKey: ["bank", bankId] });
    navigate(`/banks/${bankId}`);
  };

  if (isEdit && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 顶部栏 */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-primary text-sm">
          ← 返回
        </button>
        <h1 className="text-lg font-semibold">
          {isEdit ? "编辑题目" : "添加题目"}
        </h1>
      </div>

      {/* 表单 */}
      <div className="p-4">
        <QuestionForm
          bankId={bankId!}
          defaultValues={defaultValues}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
}
